import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { TOKENS, TEMPLATES, buildEmailHtml, exportEmailHtml, isHostedImage, setHero, type TemplateMeta } from './emailTemplate';

/**
 * The FINN-branded email, edited in place (mockup v0.5). The HTML is set ONCE per mount — React
 * never re-renders the editable DOM, so the caret is never lost. Picking another template remounts
 * the editor through its `key` in the parent. Nothing here is saved or sent yet.
 */
export function EmailEditor({
  meta,
  subject,
  signerName,
  onPickTemplate,
}: {
  meta: TemplateMeta;
  subject: string;
  signerName: string;
  /** Offers the template menu (campaign drafts). The Templates screen edits one template instead. */
  onPickTemplate?: (key: string) => void;
}) {
  const heroId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const lastEditable = useRef<HTMLElement | null>(null);
  const savedRange = useRef<Range | null>(null);
  const [menu, setMenu] = useState<'tokens' | 'templates' | 'link' | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [hero, setHeroUrl] = useState('');
  const [exported, setExported] = useState<{ html: string; warnings: string[] } | null>(null);

  // Built once from the props at mount; the parent remounts the editor (key) to start over.
  const initial = useRef(buildEmailHtml(meta, { subject, signerName }));
  useEffect(() => {
    if (rootRef.current) rootRef.current.innerHTML = initial.current;
  }, []);

  useEffect(() => {
    if (rootRef.current) setHero(rootRef.current, hero);
  }, [hero]);

  // Remember where the caret was, so a toolbar click (which takes focus) can put things back there.
  function remember() {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !rootRef.current) return;
    const range = sel.getRangeAt(0);
    const host = (range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement) as HTMLElement | null;
    const editable = host?.closest<HTMLElement>('[contenteditable="true"]');
    if (editable && rootRef.current.contains(editable)) {
      lastEditable.current = editable;
      savedRange.current = range.cloneRange();
    }
  }

  function restore(): boolean {
    const el = lastEditable.current;
    if (!el) return false;
    el.focus();
    const sel = window.getSelection();
    if (sel && savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    return true;
  }

  function command(cmd: string, arg?: string) {
    if (!restore()) return;
    // execCommand is deprecated but is still the only way to edit contenteditable with undo intact.
    document.execCommand(cmd, false, arg);
    remember();
  }

  function insertToken(token: string) {
    setMenu(null);
    command('insertText', token);
  }

  function applyLink() {
    const url = linkUrl.trim();
    setMenu(null);
    setLinkUrl('');
    if (!/^(https:\/\/|mailto:|\{\{)/i.test(url)) return;
    command('createLink', url);
  }

  return (
    <div className="rounded border border-solid border-zinc-200">
      <div className="flex flex-wrap items-center gap-1 border-b border-solid border-zinc-200 bg-zinc-50 px-2 py-1.5">
        <ToolButton label="Bold" onClick={() => command('bold')}>
          <b>B</b>
        </ToolButton>
        <ToolButton label="Italic" onClick={() => command('italic')}>
          <i>I</i>
        </ToolButton>
        <div className="relative">
          <ToolButton label="Link" onClick={() => setMenu(menu === 'link' ? null : 'link')}>
            Link
          </ToolButton>
          {menu === 'link' && (
            <div className="absolute left-0 top-full z-10 mt-1 flex w-72 gap-2 rounded border border-solid border-zinc-200 bg-white p-2 shadow">
              <input
                autoFocus
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyLink()}
                placeholder="https://…"
                aria-label="Link address"
                className="min-w-0 flex-1 rounded border border-solid border-zinc-300 px-2 py-1 text-sm"
              />
              <Button size="sm" variant="secondary" onClick={applyLink}>
                Apply
              </Button>
            </div>
          )}
        </div>
        <div className="relative">
          <ToolButton label="Insert a placeholder" onClick={() => setMenu(menu === 'tokens' ? null : 'tokens')}>
            {'{{ }}'}
          </ToolButton>
          {menu === 'tokens' && (
            <Menu>
              {TOKENS.map(([token, what]) => (
                <MenuItem key={token} title={token} sub={what} onClick={() => insertToken(token)} />
              ))}
            </Menu>
          )}
        </div>
        {onPickTemplate && (
          <div className="relative">
            <ToolButton label="Use a template" onClick={() => setMenu(menu === 'templates' ? null : 'templates')}>
              Template ▾
            </ToolButton>
            {menu === 'templates' && (
              <Menu>
                {TEMPLATES.filter((t) => !t.automated).map((t) => (
                  <MenuItem
                    key={t.key}
                    title={t.name}
                    sub={t.kind === 'rolefit' ? 'A role opened that fits them' : 'Company update, link to open roles'}
                    onClick={() => {
                      setMenu(null);
                      onPickTemplate(t.key);
                    }}
                  />
                ))}
              </Menu>
            )}
          </div>
        )}
        <span className="ml-auto" />
        <Button size="sm" variant="ghost" onClick={() => rootRef.current && setExported(exportEmailHtml(rootRef.current, subject))}>
          Export email HTML
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-solid border-zinc-200 px-3 py-2 text-sm">
        <label htmlFor={heroId} className="text-zinc-600">
          Hero image URL
        </label>
        <input
          id={heroId}
          value={hero}
          onChange={(e) => setHeroUrl(e.target.value)}
          placeholder="https://… (hosted image; empty = black band)"
          className="min-w-[220px] flex-1 rounded border border-solid border-zinc-300 px-2 py-1 text-sm"
        />
        {hero && !isHostedImage(hero) && <span className="text-xs text-amber-800">Must be a hosted https URL</span>}
      </div>

      <div
        ref={rootRef}
        data-testid="email-editor"
        onKeyUp={remember}
        onMouseUp={remember}
        onFocus={remember}
        className="max-h-[640px] overflow-auto bg-zinc-100 p-4 [&_[contenteditable=true]:focus]:outline-dashed [&_[contenteditable=true]:focus]:outline-1 [&_[contenteditable=true]:focus]:outline-blue-400 [&_[contenteditable=true]:hover]:outline-dashed [&_[contenteditable=true]:hover]:outline-1 [&_[contenteditable=true]:hover]:outline-zinc-300"
      />

      {exported && (
        <Modal open onOpenChange={(next: boolean) => !next && setExported(null)}>
          <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,48rem)] gap-4 overflow-auto rounded p-6">
            <ModalHeader>
              <ModalTitle>Email-ready HTML</ModalTitle>
            </ModalHeader>
            <p className="text-sm text-zinc-600">
              Exactly what will be sent, generated from this email. Tokens like {'{{interested_link}}'} are filled per
              candidate at send time.
            </p>
            {exported.warnings.map((w) => (
              <p key={w} role="alert" className="text-sm font-medium text-amber-800">
                {w}
              </p>
            ))}
            <textarea
              readOnly
              value={exported.html}
              aria-label="Email HTML"
              onFocus={(e) => e.currentTarget.select()}
              className="h-80 w-full rounded border border-solid border-zinc-300 p-2 font-mono text-xs"
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => void navigator.clipboard?.writeText(exported.html)}>
                Copy
              </Button>
              <Button size="sm" onClick={() => setExported(null)}>
                Close
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
}

function ToolButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      // Keep the caret in the email: a mousedown on the toolbar would otherwise take focus first.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="min-w-[32px] rounded px-2 py-1 text-sm text-zinc-700 hover:bg-zinc-200"
    >
      {children}
    </button>
  );
}

function Menu({ children }: { children: React.ReactNode }) {
  return (
    <div role="menu" className="absolute left-0 top-full z-10 mt-1 w-72 rounded border border-solid border-zinc-200 bg-white py-1 shadow">
      {children}
    </div>
  );
}

function MenuItem({ title, sub, onClick }: { title: string; sub: string; onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="block w-full px-3 py-2 text-left hover:bg-zinc-50"
    >
      <span className="block text-sm font-medium text-zinc-900">{title}</span>
      <span className="block text-xs text-zinc-500">{sub}</span>
    </button>
  );
}
